import type { Dispatch, SetStateAction } from "react";
import { useCallback } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { DemoDisabledTooltip } from "app/pages/sales-intelligence/common-components/demo-mode/DemoDisabledTooltip";
import type { ActivatedChildComponentType } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    StyledInnerContainer,
    TableActionsPanel,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { useDemoModeDisabled } from "app/pages/sales-intelligence/hooks/demo-mode/useDemoModeDisabled";
import {
    SaveToListButtonContainer,
    selectActiveLeadsType,
    useColumnSelectionMutation,
} from "app/pages/sales-intelligence/modules/leads-search";
import { useColumnsConfigQuery } from "app/pages/sales-intelligence/modules/leads-search/query/queries/useColumnsConfigQuery";
import leadsSearchTrackingService from "app/pages/sales-intelligence/modules/leads-search/services/tracking/trackingService";
import { selectIsExcludeByOpportunitiesSelected } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { advancedSearchQueryKeyFactory } from "app/pages/sales-intelligence/pages/advanced-search/query/queryKeyFactory";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { contactsQueryKeys } from "app/pages/sales-intelligence/sub-modules/contacts-search/query/queryKeys/queryKeyFactory";
import { allTrackers } from "app/services/track/track";

import { useLeadsResultsActionsState } from "../../../query/queries/useLeadsResultsActionsState";
import SaveToListPanelContainer from "../SaveToListPanel/SaveToListPanelContainer";
import SelectionModalContainer from "../SelectionModal/SelectionModalContainer";
import TableColumnsSelectionContainer from "../TableColumnsSelection/TableColumnsSelectionContainer";
import TableExportOptionsContainer from "../TableExportOptions/TableExportOptionsContainer";

import { tableActionsI18KeyModifier } from "./const";

const TableActionsPanelContainer = () => {
    const queryClient = useQueryClient();
    const activeLeadsType = useSelector(selectActiveLeadsType);
    const isExcludeByOpportunitiesSelected = useSelector(selectIsExcludeByOpportunitiesSelected);
    const { isSuccess: isColumnConfigReady } = useColumnsConfigQuery();

    const { areActionsDisabled } = useLeadsResultsActionsState(activeLeadsType);
    const { mutateAsync, isPending } = useColumnSelectionMutation();

    // In demo mode the results are a read-only preview: write actions are disabled while CSV/Excel export stays
    // available, so export keeps the original disabled state and the write actions add the demo reason.
    const { isDisabled: isDemoMode } = useDemoModeDisabled();
    const areWriteActionsDisabled = areActionsDisabled || isDemoMode;

    const handleStayOnPage = (): void => {
        allTrackers.dispatchTrackEvent("multi select actions", "stay on page", "save successful popup/cancel");

        if (!isExcludeByOpportunitiesSelected) {
            return;
        }

        void queryClient.invalidateQueries({
            queryKey: advancedSearchQueryKeyFactory.tableData(),
        });

        void queryClient.invalidateQueries({
            queryKey: contactsQueryKeys.baseTableData(),
        });
    };

    const handleGoToList = () => trackingService.trackMultiSelectorSuccessMovedPopup("go to list");

    const handleSaveToList = (activate: Dispatch<SetStateAction<ActivatedChildComponentType>>): void => {
        return activate(() => SaveToListPanelContainer);
    };

    const handleSuggestionsApply = useCallback(
        async (columns: readonly string[]) => {
            await mutateAsync({ columns });
        },
        [mutateAsync],
    );

    return (
        <TableActionsPanel>
            {({ activate }) => (
                <>
                    <StyledInnerContainer>
                        <DemoDisabledTooltip>
                            <SaveToListButtonContainer
                                isDisabled={areWriteActionsDisabled}
                                i18nKeyModifier={tableActionsI18KeyModifier[activeLeadsType]}
                                onClick={() => handleSaveToList(activate)}
                                onStayOnPage={handleStayOnPage}
                                onGoToList={handleGoToList}
                                onActivate={activate}
                                onHookClick={() => leadsSearchTrackingService.trackSaveToListHookClick()}
                            />
                        </DemoDisabledTooltip>
                        <TableExportOptionsContainer
                            isDisabled={areActionsDisabled}
                            isCrmExportDisabled={isDemoMode}
                            onActivate={activate}
                        />
                    </StyledInnerContainer>

                    <DemoDisabledTooltip>
                        <TableColumnsSelectionContainer
                            isDisabled={areWriteActionsDisabled || !isColumnConfigReady || isPending}
                            renderSelectionModal={SelectionModalContainer}
                            onSuggestionsApply={handleSuggestionsApply}
                        />
                    </DemoDisabledTooltip>
                </>
            )}
        </TableActionsPanel>
    );
};

export default TableActionsPanelContainer;
